import { useId, useState } from "react";

import { dayLabel, todayStr } from "../domain/date";
import { curWeight } from "../domain/energy";
import { useKaya } from "../store/kayaStore";
import { ActionBar } from "./ActionBar";
import { Sheet } from "./Sheet";

/**
 * Log a weigh-in: date, weight, waist (optional). Owned by the Progress pages; Today's weigh-in
 * row opens the same sheet. Steps are never entered here (they belong to Today › Activity).
 */
export function LogWeightSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Mounted only while open, so every opening starts from today and empty boxes.
  return open ? <LogWeightForm onClose={onClose} /> : null;
}

function LogWeightForm({ onClose }: { onClose: () => void }) {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const checkBadges = useKaya((s) => s.checkBadges);
  const ids = { date: useId(), w: useId(), waist: useId() };

  const [date, setDate] = useState(todayStr());
  const [w, setW] = useState("");
  const [waist, setWaist] = useState("");

  const byDate = S.measures.slice().sort((a, b) => (a.date < b.date ? 1 : -1));
  const lastW = byDate.find((m) => m.w);
  const lastWa = byDate.find((m) => m.waist);

  const wN = parseFloat(w);
  const waN = parseFloat(waist);
  const okW = Number.isFinite(wN) && wN > 0;
  const okWa = Number.isFinite(waN) && waN > 0;
  const reason = !date ? "Pick a date" : !okW && !okWa ? "Enter a weight or a waist" : undefined;

  const save = () => {
    if (reason) return;
    update((d) => {
      let i = d.measures.findIndex((x) => x.date === date);
      if (i < 0) i = d.measures.push({ date }) - 1;
      if (okW) d.measures[i].w = wN;
      if (okWa) d.measures[i].waist = waN;
    });
    showToast("Saved");
    checkBadges();
    onClose();
  };

  return (
    <Sheet
      title="Log weight"
      sub="Steps are logged on Today, not here."
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save weigh-in", onClick: save, disabled: reason !== undefined, reason }}
        />
      }
    >
      <div className="field">
        <label htmlFor={ids.date}>Date</label>
        <input id={ids.date} type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={ids.w}>Weight (kg)</label>
        <input
          id={ids.w}
          type="number"
          step="0.1"
          inputMode="decimal"
          placeholder={lastW?.w ? String(lastW.w) : String(curWeight(S) || "")}
          value={w}
          onChange={(e) => setW(e.target.value)}
        />
        {lastW?.w ? (
          <small className="fieldhint">
            Last: {lastW.w.toFixed(1)} kg on {dayLabel(lastW.date)}
          </small>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor={ids.waist}>Waist at navel, optional (cm)</label>
        <input
          id={ids.waist}
          type="number"
          step="0.5"
          inputMode="decimal"
          placeholder={lastWa?.waist ? String(lastWa.waist) : ""}
          value={waist}
          onChange={(e) => setWaist(e.target.value)}
        />
        {lastWa?.waist ? (
          <small className="fieldhint">
            Last: {lastWa.waist} cm on {dayLabel(lastWa.date)}
          </small>
        ) : null}
      </div>
    </Sheet>
  );
}
